import React from 'react';
import { StandardTableActions } from '../../components/ui';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import { MarketingClient } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { DocumentTextIcon } from '../../components/ui/Icons';

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Marketing',
    description: 'Manage your marketing contacts database',
    permissionModuleKey: 'marketing',
    route: {
      list: '/marketing',
      new: '/marketing/new',
      detail: (id: number) => `/marketing/${id}`,
      edit: (id: number) => `/marketing/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/crm-clients/marketing',
      detail: (id: number) => `/crm-clients/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search marketing contacts by name, email, phone, or address...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'clientName', 'clientName2', 'contactPerson', 'gemiNumber', 'dateStarted', 'sourceUrl', 'clientCalls'
      ],
    },
    {
      name: 'contact',
      label: 'Contact',
      icon: '☎️',
      fields: [
        'clientEmail', 'clientEmail2', 'clientPhone', 'clientPhone2', 'clientWebSite'
      ],
    },
    {
      name: 'location',
      label: 'Location',
      icon: '📍',
      fields: [
        'clientAddress', 'clientCity', 'clientZipCode', 'clientState', 'clientCountryId'
      ],
    },
    {
      name: 'business',
      label: 'Business',
      icon: '💼',
      fields: [
        'clientVATNumber', 'clientPaymentTerms', 'clientTaxAuthority', 'clientNotes'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'clientName',
        label: 'Name',
        placeholder: 'All Names',
        getValue: (item: MarketingClient) => item.clientName,
        options: [],
      },
      {
        key: 'clientName2',
        label: 'Name 2',
        placeholder: 'All Names 2',
        getValue: (item: MarketingClient) => item.clientName2,
        options: [],
      },
      {
        key: 'contactPerson',
        label: 'Contact Person',
        placeholder: 'All Contact Persons',
        getValue: (item: MarketingClient) => item.contactPerson,
        options: [],
      },
      {
        key: 'clientEmail',
        label: 'Email',
        placeholder: 'All Emails',
        getValue: (item: MarketingClient) => item.clientEmail,
        options: [],
      },
      {
        key: 'clientEmail2',
        label: 'Email 2',
        placeholder: 'All Emails 2',
        getValue: (item: MarketingClient) => item.clientEmail2,
        options: [],
      },
      {
        key: 'clientPhone',
        label: 'Phone',
        placeholder: 'All Phones',
        getValue: (item: MarketingClient) => item.clientPhone,
        options: [],
      },
      {
        key: 'clientPhone2',
        label: 'Phone 2',
        placeholder: 'All Phones 2',
        getValue: (item: MarketingClient) => item.clientPhone2,
        options: [],
      },
      {
        key: 'clientAddress',
        label: 'Address',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: MarketingClient) => item.clientAddress,
        options: [],
      },
      {
        key: 'clientCity',
        label: 'City',
        placeholder: 'All Cities',
        getValue: (item: MarketingClient) => item.clientCity,
        options: [],
      },
      {
        key: 'clientZipCode',
        label: 'Zip Code',
        placeholder: 'All Zip Codes',
        getValue: (item: MarketingClient) => item.clientZipCode,
        options: [],
      },
      {
        key: 'clientState',
        label: 'State',
        placeholder: 'All States',
        getValue: (item: MarketingClient) => item.clientState,
        options: [],
      },
      {
        key: 'clientCountryId',
        label: 'Country',
        placeholder: 'All Countries',
        type: 'select' as const,
        getValue: (item: MarketingClient) => 
          item.clientCountryDescr?.pc_descr || (item.clientCountryId ? `Country #${item.clientCountryId}` : ''),
        options: [],
        apiEndpoint: '/p-country',
        labelField: 'pc_descr',
      },
      {
        key: 'clientWebSite',
        label: 'Website',
        placeholder: 'All Websites',
        getValue: (item: MarketingClient) => item.clientWebSite,
        options: [],
      },
      {
        key: 'clientVATNumber',
        label: 'VAT Number',
        placeholder: 'All VAT Numbers',
        getValue: (item: MarketingClient) => item.clientVATNumber,
        options: [],
      },
      {
        key: 'clientPaymentTerms',
        label: 'Payment Terms',
        placeholder: 'All Payment Terms',
        type: 'number' as const,
        getValue: (item: MarketingClient) => item.clientPaymentTerms?.toString(),
        options: [],
      },
      {
        key: 'clientTaxAuthority',
        label: 'Tax Authority',
        placeholder: 'All Tax Authorities',
        type: 'select' as const,
        getValue: (item: MarketingClient) => 
          item.clientTaxAuthorityDescr?.pt_descr || (item.clientTaxAuthority ? `Tax Authority #${item.clientTaxAuthority}` : ''),
        options: [],
        apiEndpoint: '/p-tax-authority',
        labelField: 'pt_descr',
      },
      {
        key: 'gemiNumber',
        label: 'GEMI Number',
        placeholder: 'All GEMI Numbers',
        getValue: (item: MarketingClient) => item.gemiNumber,
        options: [],
      },
      {
        key: 'dateStarted',
        label: 'Date Started',
        placeholder: 'All Date Started',
        type: 'date' as const,
        getValue: (item: MarketingClient) =>
          item.dateStarted
            ? formatDateGlobal(item.dateStarted)
            : undefined,
        options: [],
      },
      {
        key: 'sourceUrl',
        label: 'Source URL',
        placeholder: 'Type to filter URL...',
        type: 'text' as const,
        getValue: (item: MarketingClient) => item.sourceUrl,
        options: [],
      },
      {
        key: 'clientNotes',
        label: 'Notes',
        placeholder: 'Type to filter...',
        type: 'text' as const,
        getValue: (item: MarketingClient) => item.clientNotes,
        options: [],
      },
      {
        key: 'clientCalls',
        label: 'Calls',
        placeholder: 'All Calls',
        type: 'boolean' as const,
        getValue: (item: MarketingClient) => ((item._count?.clientCalls || 0) > 0 ? 'Has calls' : 'Does not have calls'),
        options: ['Has calls', 'Does not have calls'],
      },
    ],
    applyFilter: (
      items: MarketingClient[],
      filters: {
        clientName: string[];
        clientName2: string[];
        contactPerson: string[];
        clientEmail: string[];
        clientEmail2: string[];
        clientPhone: string[];
        clientPhone2: string[];
        clientAddress: string[] | string;
        clientCity: string[];
        clientZipCode: string[];
        clientState: string[];
        clientCountryId: string[];
        clientWebSite: string[];
        clientVATNumber: string[];
        clientPaymentTerms: string[];
        clientTaxAuthority: string[];
        gemiNumber: string[];
        dateStarted: string[];
        sourceUrl: string[] | string;
        clientNotes: string[] | string;
        clientCalls: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Client Name filter
        if (filters.clientName.length > 0) {
          const value = (item.clientName || '').toLowerCase();
          const matches = filters.clientName.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Client Name 2 filter
        if (filters.clientName2.length > 0) {
          const value = (item.clientName2 || '').toLowerCase();
          const matches = filters.clientName2.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Contact Person filter
        if (filters.contactPerson.length > 0) {
          const value = (item.contactPerson || '').toLowerCase();
          const matches = filters.contactPerson.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Email filter
        if (filters.clientEmail.length > 0) {
          const value = (item.clientEmail || '').toLowerCase();
          const matches = filters.clientEmail.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Email 2 filter
        if (filters.clientEmail2.length > 0) {
          const value = (item.clientEmail2 || '').toLowerCase();
          const matches = filters.clientEmail2.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Phone filter
        if (filters.clientPhone.length > 0) {
          const value = (item.clientPhone || '').toLowerCase();
          const matches = filters.clientPhone.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Phone 2 filter
        if (filters.clientPhone2.length > 0) {
          const value = (item.clientPhone2 || '').toLowerCase();
          const matches = filters.clientPhone2.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Address filter (text filter)
        const addressFilter = filters.clientAddress;
        if (typeof addressFilter === 'string' && addressFilter.trim() !== '') {
          const value = (item.clientAddress || '').toLowerCase();
          if (!value.includes(addressFilter.toLowerCase())) return false;
        } else if (Array.isArray(addressFilter) && addressFilter.length > 0) {
          const value = (item.clientAddress || '').toLowerCase();
          const matches = addressFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // City filter
        if (filters.clientCity.length > 0) {
          const value = (item.clientCity || '').toLowerCase();
          const matches = filters.clientCity.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Zip Code filter
        if (filters.clientZipCode.length > 0) {
          const value = (item.clientZipCode || '').toLowerCase();
          const matches = filters.clientZipCode.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // State filter
        if (filters.clientState.length > 0) {
          const value = (item.clientState || '').toLowerCase();
          const matches = filters.clientState.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Country ID filter
        if (filters.clientCountryId.length > 0) {
          const displayValue = item.clientCountryDescr?.pc_descr || (item.clientCountryId ? `Country #${item.clientCountryId}` : '');
          const matches = filters.clientCountryId.some((filter) => {
            return displayValue === filter ||
                   (item.clientCountryId && filter === `Country #${item.clientCountryId}`) ||
                   (item.clientCountryDescr?.pc_descr && filter === item.clientCountryDescr.pc_descr);
          });
          if (!matches) return false;
        }

        // Website filter
        if (filters.clientWebSite.length > 0) {
          const value = (item.clientWebSite || '').toLowerCase();
          const matches = filters.clientWebSite.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // VAT Number filter
        if (filters.clientVATNumber.length > 0) {
          const value = (item.clientVATNumber || '').toLowerCase();
          const matches = filters.clientVATNumber.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Payment Terms filter
        if (filters.clientPaymentTerms.length > 0) {
          const value = item.clientPaymentTerms?.toString() || '';
          const matches = filters.clientPaymentTerms.some((filter) => {
            return value === filter || filter === value;
          });
          if (!matches) return false;
        }

        // Tax Authority filter
        if (filters.clientTaxAuthority.length > 0) {
          const displayValue = item.clientTaxAuthorityDescr?.pt_descr || (item.clientTaxAuthority ? `Tax Authority #${item.clientTaxAuthority}` : '');
          const matches = filters.clientTaxAuthority.some((filter) => {
            return displayValue === filter ||
                   (item.clientTaxAuthority && filter === `Tax Authority #${item.clientTaxAuthority}`) ||
                   (item.clientTaxAuthorityDescr?.pt_descr && filter === item.clientTaxAuthorityDescr.pt_descr);
          });
          if (!matches) return false;
        }

        // GEMI Number filter
        if (filters.gemiNumber.length > 0) {
          const value = (item.gemiNumber || '').toLowerCase();
          const matches = filters.gemiNumber.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Date Started filter
        if (filters.dateStarted.length > 0) {
          if (ranges.dateStarted) {
            const range = ranges.dateStarted;
            if (range.start && range.end && item.dateStarted) {
              const itemDate = new Date(item.dateStarted);
              if (itemDate < range.start || itemDate > range.end) return false;
            }
          } else {
            const value = item.dateStarted
              ? formatDateGlobal(item.dateStarted)
              : '';
            const matches = filters.dateStarted.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) return false;
          }
        }

        // Source URL filter (text filter)
        const urlFilter = filters.sourceUrl;
        if (typeof urlFilter === 'string' && urlFilter.trim() !== '') {
          const value = (item.sourceUrl || '').toLowerCase();
          if (!value.includes(urlFilter.toLowerCase())) return false;
        } else if (Array.isArray(urlFilter) && urlFilter.length > 0) {
          const value = (item.sourceUrl || '').toLowerCase();
          const matches = urlFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Notes filter (text filter)
        const notesFilter = filters.clientNotes;
        if (typeof notesFilter === 'string' && notesFilter.trim() !== '') {
          const value = (item.clientNotes || '').toLowerCase();
          if (!value.includes(notesFilter.toLowerCase())) return false;
        } else if (Array.isArray(notesFilter) && notesFilter.length > 0) {
          const value = (item.clientNotes || '').toLowerCase();
          const matches = notesFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Calls filter
        if (filters.clientCalls.length > 0) {
          const value = (item._count?.clientCalls || 0) > 0 ? 'Has calls' : 'Does not have calls';
          const matches = filters.clientCalls.some((filter) => value === String(filter));
          if (!matches) return false;
        }

        return true;
      });
    },
  },

  // Toolbar Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            },
          },
        ],
      },
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Marketing Contact',
        showTitle: false,
        variant: 'primary' as const,
        href: '/marketing/new',
      },
    ],
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: MarketingClient) => {
      window.location.href = `/marketing/${item.clientId}`;
    },
    keyExtractor: (item: MarketingClient) => item.clientId,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new marketing contact.',
    noSearchResults: 'No marketing contacts found matching your search criteria.',
    noFilterResults: 'No marketing contacts match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete Marketing Contact',
    message: 'Are you sure you want to delete this marketing contact? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete marketing contact. Please try again.',
    handler: async (id: number) => {
      const { deleteClient } = await import('../../services/mutations');
      await deleteClient(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },

  // Detail View Configuration
  detail: {
    subtitle: 'Marketing Contact Details',
    notFoundTitle: 'Marketing contact not found',
    notFoundMessage: 'The marketing contact you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Marketing',
    defaultTitle: 'Unnamed Marketing Contact',
    getTitle: (item: MarketingClient) => {
      return item.clientName || `Marketing Contact #${item.clientId}`;
    },
  },

  // Form Configuration
  form: {
    new: {
      title: 'New Marketing Contact',
      subtitle: 'Add a new marketing contact to your database',
      submitText: 'Create Marketing Contact',
    },
    edit: {
      title: 'Edit Marketing Contact',
      submitText: 'Update Marketing Contact',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading marketing contact...',
    errorMessage: 'Failed to save marketing contact. Please try again.',
    create: async (data: Partial<MarketingClient>) => {
      const { createMarketingClient } = await import('../../services/mutations');
      // Ensure isClient is false for marketing
      const marketingData = { ...data, isClient: false };
      return await createMarketingClient(marketingData);
    },
    update: async (id: number, data: Partial<MarketingClient>) => {
      const { updateClient } = await import('../../services/mutations');
      return await updateClient(id, data);
    },
  },

  // Unified Field Configuration
  fields: [
    {
      name: 'clientName',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter name',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255,
      },
    },
    {
      name: 'clientName2',
      label: 'Name 2',
      type: 'text',
      placeholder: 'Enter name 2',
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'contactPerson',
      label: 'Contact Person',
      type: 'text',
      placeholder: 'Enter contact person name',
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'clientEmail',
      label: 'Email',
      type: 'email',
      placeholder: 'Enter email address',
      validation: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address',
      },
    },
    {
      name: 'clientEmail2',
      label: 'Email 2',
      type: 'email',
      placeholder: 'Enter secondary email address',
      validation: {
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address',
      },
    },
    {
      name: 'clientPhone',
      label: 'Phone',
      type: 'tel',
      placeholder: 'Enter phone number',
      validation: {
        maxLength: 50,
      },
    },
    {
      name: 'clientPhone2',
      label: 'Phone 2',
      type: 'tel',
      placeholder: 'Enter secondary phone number',
      validation: {
        maxLength: 50,
      },
    },
    {
      name: 'clientWebSite',
      label: 'Website',
      type: 'url',
      placeholder: 'Enter website URL',
      validation: {
        maxLength: 255,
      },
    },
    {
      name: 'clientAddress',
      label: 'Address',
      type: 'text',
      placeholder: 'Enter street address',
      validation: {
        maxLength: 500,
      },
    },
    {
      name: 'clientCity',
      label: 'City',
      type: 'text',
      placeholder: 'Enter city',
      validation: {
        maxLength: 100,
      },
    },
    {
      name: 'clientZipCode',
      label: 'Zip Code',
      type: 'text',
      placeholder: 'Enter zip code',
      validation: {
        maxLength: 20,
      },
    },
    {
      name: 'clientState',
      label: 'State',
      type: 'text',
      placeholder: 'Enter state',
      validation: {
        maxLength: 100,
      },
    },
    {
      name: 'clientCountryId',
      label: 'Country',
      type: 'select',
      placeholder: 'Select country',
      options: [], // Will be populated dynamically in Form.tsx
    },
    {
      name: 'clientVATNumber',
      label: 'VAT Number',
      type: 'text',
      placeholder: 'Enter VAT number',
      validation: {
        maxLength: 50,
      },
    },
    {
      name: 'clientPaymentTerms',
      label: 'Payment Terms',
      type: 'number',
      placeholder: 'Enter payment terms',
      validation: {
        min: 0,
      },
    },
    {
      name: 'clientTaxAuthority',
      label: 'Tax Authority',
      type: 'select',
      placeholder: 'Select tax authority',
      options: [], // Will be populated dynamically in Form.tsx
    },
    {
      name: 'clientNotes',
      label: 'Notes',
      type: 'textarea',
      placeholder: 'Enter notes',
      rows: 4,
      validation: {
        maxLength: 5000,
      },
    },
    {
      name: 'gemiNumber',
      label: 'GEMI Number',
      type: 'text',
      placeholder: 'GEMI Number',
      readOnly: true,
      disabled: true,
    },
    {
      name: 'dateStarted',
      label: 'Date Started',
      type: 'date',
      placeholder: 'Date Started',
      readOnly: true,
      disabled: true,
    },
    {
      name: 'sourceUrl',
      label: 'Source URL',
      type: 'url',
      placeholder: 'Source URL',
      readOnly: true,
      disabled: true,
    },
    {
      name: 'clientIsActive',
      label: 'Is Active',
      type: 'switch',
      switchLabel: 'Marketing contact is active',
      description: 'Controls whether this marketing contact is active.',
      labelPosition: 'left',
    },
    {
      name: 'isClient',
      label: 'Is Client',
      type: 'switch',
      switchLabel: 'This marketing contact is also a client',
      description: 'If enabled, this record becomes a client and may no longer appear in the Marketing list.',
      labelPosition: 'left',
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        icon: '📋',
        gridCols: 'md:grid-cols-2',
        fields: ['clientName', 'clientName2', 'contactPerson'],
        collapsible: true,
      },
      {
        id: 'contact',
        title: 'Contact Information',
        icon: '☎️',
        gridCols: 'md:grid-cols-12',
        fields: ['clientEmail', 'clientEmail2', 'clientPhone', 'clientPhone2', 'clientWebSite'],
        fieldSpans: {
          clientEmail: 'md:col-span-6',
          clientEmail2: 'md:col-span-6',
          clientPhone: 'md:col-span-6',
          clientPhone2: 'md:col-span-6',
          clientWebSite: 'md:col-span-12',
        },
        collapsible: true,
      },
      {
        id: 'address',
        title: 'Address Information',
        icon: '📍',
        gridCols: 'md:grid-cols-12',
        fields: ['clientAddress', 'clientCity', 'clientZipCode', 'clientState', 'clientCountryId'],
        fieldSpans: {
          clientAddress: 'md:col-span-12',
          clientCity: 'md:col-span-6',
          clientZipCode: 'md:col-span-3',
          clientState: 'md:col-span-3',
          clientCountryId: 'md:col-span-6',
        },
        collapsible: true,
      },
      {
        id: 'business',
        title: 'Business Information',
        icon: '💼',
        gridCols: 'md:grid-cols-12',
        fields: ['clientVATNumber', 'clientPaymentTerms', 'clientTaxAuthority', 'clientNotes'],
        fieldSpans: {
          clientVATNumber: 'md:col-span-6',
          clientPaymentTerms: 'md:col-span-6',
          clientTaxAuthority: 'md:col-span-6',
          clientNotes: 'md:col-span-12',
        },
        collapsible: true,
      },
      {
        id: 'system',
        title: 'System Information',
        icon: '⚙️',
        gridCols: 'md:grid-cols-2',
        fields: ['gemiNumber', 'dateStarted', 'sourceUrl', 'clientIsActive', 'isClient'],
        collapsible: true,
      },
    ],
  } as FormLayout,
};

// Helper function to get columns
export const getColumns = (formatDate: (dateString?: string) => string): Column<MarketingClient>[] => [
  {
    key: 'clientName',
    header: 'Name',
    sortable: true,
    render: (_value, row) => {
      const attachmentCount = row.FileAttachment?.length ?? row._count?.FileAttachment ?? 0;
      const clientName = row.clientName || 'Unnamed Contact';
      const truncatedName = clientName.length > 50 ? clientName.substring(0, 50) + '...' : clientName;
      const count = row._count?.clientCalls || 0;
      const lastCallDate = row.clientCalls?.[0]?.clientCallDate;
      const appointmentsCount = row._count?.clientAppointments ?? 0;
      const lastAppointmentDate = row.clientAppointments?.[0]?.clientAppScheduleDateTime;
      const hasSecondLine =
        !!row.contactPerson || count > 0 || !!lastCallDate || appointmentsCount > 0 || !!lastAppointmentDate;

      return (
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="truncate">
              {row.clientVATNumber ? `${truncatedName} (${row.clientVATNumber})` : truncatedName}
            </span>
            {attachmentCount > 0 && (
              <DocumentTextIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
            )}
          </div>
          {hasSecondLine && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 min-w-0 text-xs text-gray-500 dark:text-gray-400">
              {row.contactPerson && <span className="truncate">{row.contactPerson}</span>}
              {row.contactPerson && (count > 0 || appointmentsCount > 0) && <span>-</span>}
              {count > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:text-gray-300">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.129a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.209-.503l4.493 1.498A1 1 0 0121 15.72V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  {count}
                </span>
              )}
              {count > 0 && lastCallDate && <span>{formatDate(lastCallDate)}</span>}
              {appointmentsCount > 0 && (
                <>
                  <Link
                    to="/appointments"
                    state={row.clientId != null ? { clientId: row.clientId } : undefined}
                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                    title={`${appointmentsCount} appointment${appointmentsCount !== 1 ? 's' : ''}`}
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    {appointmentsCount}
                  </Link>
                  {lastAppointmentDate && <span>Last: {formatDate(lastAppointmentDate)}</span>}
                </>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'clientAddress',
    header: 'Address',
    sortable: true,
    render: (value, row) => {
      const addressLine = value
        ? (String(value).length > 50 ? String(value).substring(0, 50) + '...' : String(value))
        : '-';
      const locationParts = [
        row.clientCity,
        row.clientZipCode,
        row.clientState,
        row.clientCountryDescr?.pc_descr || (row.clientCountryId ? String(row.clientCountryId) : ''),
      ].filter(Boolean);

      return (
        <div className="min-w-0">
          <div>{addressLine}</div>
          {locationParts.length > 0 && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {locationParts.join(', ')}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'clientTaxAuthority',
    header: 'Tax',
    sortable: true,
    render: (value, row) => (
      row.clientTaxAuthorityDescr?.pt_descr || '-'
    ),
  },
  {
    key: 'clientWebSite',
    header: 'Web',
    sortable: true,
    render: (value) => (
      value ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          title={String(value)}
          aria-label={`Open website ${String(value)}`}
          className="inline-flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 115.656 5.656l-1.5 1.5"
            />
          </svg>
        </a>
      ) : (
        '-'
      )
    ),
  },
  {
    key: 'clientPhone',
    header: 'Phone',
    sortable: true,
    render: (value, row) => (
      <div className="min-w-0">
        <div>{value || '-'}</div>
        {row.clientPhone2 && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {row.clientPhone2}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'clientEmail',
    header: 'Email',
    sortable: true,
    render: (value, row) => (
      <div className="min-w-0">
        <div>{value || '-'}</div>
        {row.clientEmail2 && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {row.clientEmail2}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'clientVATNumber',
    header: 'Identification',
    sortable: true,
    render: (_value, row) => (
      <div className="min-w-0">
        <div>{row.gemiNumber || '-'}</div>
        {row.clientCode && (
          <div className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 truncate">
            <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 4h14a2 2 0 012 2v7H3V6a2 2 0 012-2z"
              />
            </svg>
            <span>{row.clientCode}</span>
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'clientPaymentTerms',
    header: 'Payment Terms',
    sortable: true,
    render: (value) => (
      value || '-'
    ),
  },
  {
    key: 'clientAppointments',
    header: 'A',
    sortable: false,
    render: (value, row) => {
      const count = (row as any)._count?.clientAppointments || 0;
      return count > 0 ? (
        count
      ) : (
        '-'
      );
    },
  },
];

// Helper function to get detail sections
export const getDetailSections = (
  item: MarketingClient,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📋',
      fields: [
        {
          label: 'Name',
          value: item.clientName || '-',
        },
        {
          label: 'Name 2',
          value: item.clientName2 || '-',
        },
        {
          label: 'GEMI Number',
          value: item.gemiNumber || '-',
        },
        {
          label: 'Date Started',
          value: formatDate(item.dateStarted),
        },
        {
          label: 'Source URL',
          value: item.sourceUrl ? (
            <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
              {item.sourceUrl}
            </a>
          ) : '-',
        },
        {
          label: 'Contact Person',
          value: item.contactPerson || '-',
        },
      ],
    },
    {
      title: 'Address Information',
      icon: '📍',
      fields: [
        {
          label: 'Address',
          value: item.clientAddress || '-',
        },
        {
          label: 'City',
          value: item.clientCity || '-',
        },
        {
          label: 'Zip Code',
          value: item.clientZipCode || '-',
        },
        {
          label: 'State',
          value: item.clientState || '-',
        },
        {
          label: 'Tax Authority',
          value: item.clientTaxAuthorityDescr?.pt_descr || '-',
        },
        {
          label: 'Country',
          value: item.clientCountryDescr?.pc_descr || '-',
        },
      ],
    },
    {
      title: 'Contact Information',
      icon: '☎️',
      fields: [
        {
          label: 'Website',
          value: item.clientWebSite || '-',
        },
        {
          label: 'Phone',
          value: item.clientPhone || '-',
        },
        {
          label: 'Phone 2',
          value: item.clientPhone2 || '-',
        },
        {
          label: 'Email',
          value: item.clientEmail || '-',
        },
        {
          label: 'Email 2',
          value: item.clientEmail2 || '-',
        },
      ],
    },
    {
      title: 'Business Information',
      icon: '💼',
      fields: [
        {
          label: 'VAT Number',
          value: item.clientVATNumber || '-',
        },
        {
          label: 'Payment Terms',
          value: item.clientPaymentTerms || '-',
        },
        {
          label: 'Notes',
          value: item.clientNotes || '-',
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (item: MarketingClient) => {
  return null; // Marketing doesn't use status badges
};

// Helper function to get toolbar icons
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to get table actions
export const getTableActions = (
  item: MarketingClient,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.clientId)}
      editTo={moduleConfig.module.route.edit(item.clientId)}
      onDelete={() => onDelete(item.clientId)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};


export const getInitialFormData = (): Partial<MarketingClient> => {
  return {
    clientName: '',
    clientName2: '',
    contactPerson: '',
    clientAddress: '',
    clientCity: '',
    clientZipCode: '',
    clientState: '',
    clientWebSite: '',
    clientPhone: '',
    clientPhone2: '',
    clientEmail: '',
    clientEmail2: '',
    clientNotes: '',
    clientVATNumber: '',
    clientPaymentTerms: undefined,
    clientTaxAuthority: undefined,
    clientCountryId: undefined,
    isClient: false, // Always false for marketing
    clientIsActive: true,
    gemiNumber: '',
    sourceUrl: '',
  };
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: MarketingClient): Partial<MarketingClient> => {
  return {
    clientName: item.clientName || '',
    clientName2: item.clientName2 || '',
    contactPerson: item.contactPerson || '',
    clientAddress: item.clientAddress || '',
    clientCity: item.clientCity || '',
    clientZipCode: item.clientZipCode || '',
    clientState: item.clientState || '',
    clientWebSite: item.clientWebSite || '',
    clientPhone: item.clientPhone || '',
    clientPhone2: item.clientPhone2 || '',
    clientEmail: item.clientEmail || '',
    clientEmail2: item.clientEmail2 || '',
    clientNotes: item.clientNotes || '',
    clientVATNumber: item.clientVATNumber || '',
    clientPaymentTerms: item.clientPaymentTerms,
    clientTaxAuthority: item.clientTaxAuthority,
    clientCountryId: item.clientCountryId,
    isClient: false, // Always false for marketing
    clientIsActive: item.clientIsActive ?? true,
    gemiNumber: item.gemiNumber || '',
    dateStarted: item.dateStarted,
    sourceUrl: item.sourceUrl || '',
  };
};

